import { useEffect, useMemo, useState } from "react";
import { toast } from "@/lib/toast";
import { Button } from "@mcpjam/design-system/button";
import { Badge } from "@mcpjam/design-system/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@mcpjam/design-system/sheet";
import {
  Check,
  Loader2,
  Plus,
  Trash2,
  Hammer,
  Users,
  ChevronLeft,
} from "lucide-react";
import {
  useCreateSandboxImage,
  useDeleteSandboxImage,
  useSandboxImages,
  usePromoteSandboxImage,
  useSetComputerSandboxImage,
  useStartSandboxImageBuild,
  useUpdateSandboxImage,
  type SandboxImageView,
} from "@/hooks/useSandboxImages";
import { EnvironmentBuildBadge } from "./EnvironmentBuildBadge";
import { convexErrMessage } from "@/lib/convex-error";

// Ships a real, buildable default so "Create → Build" works out of the box.
// The base must be an allowlisted official image (debian, ubuntu, node,
// python) pinned by @sha256 digest — swap in your own base/digest as needed.
// Only FROM + RUN are supported today.
const NEW_ENVIRONMENT_TEMPLATE = `# Allowlisted official base (debian, ubuntu, node, python) pinned by digest.
# Only FROM + RUN are supported today. Change the base or digest as needed.
FROM debian:bookworm-slim@sha256:60eac759739651111db372c07be67863818726f754804b8707c90979bda511df
RUN echo "customize me"
`;

// Convex error shaping (e.g. the backend's DockerfileValidationError) lives
// in the shared util now that project environments reuse the same pattern.
const errMessage = convexErrMessage;

/**
 * Manage the project's Computer sandbox images and which one this computer boots
 * from. Opened from the Computer tab's "Change image" control. Builds stream
 * reactively via Convex queries, so no manual polling.
 */
export function SandboxImagesDrawer({
  open,
  onOpenChange,
  projectId,
  attachedEnvironmentId,
  canAttach,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  attachedEnvironmentId: string | null;
  /** Whether the computer is in a state that can accept an image change
   * (settled or not-yet-provisioned) — mirrors Reset's gating. */
  canAttach: boolean;
}) {
  const environments = useSandboxImages(open ? projectId : null);
  const setComputerEnvironment = useSetComputerSandboxImage();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  // A just-created env, kept until the (reactive) list query includes it — so
  // we land on its detail immediately instead of bouncing back to the list.
  const [justCreated, setJustCreated] = useState<SandboxImageView | null>(null);

  const selected = useMemo(
    () =>
      environments?.find((e) => e.environmentId === selectedId) ??
      (justCreated?.environmentId === selectedId ? justCreated : null),
    [environments, selectedId, justCreated]
  );

  // Detail / new-env editor lives below the list on mobile-narrow drawers.
  const showDetail = creating || selected !== null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 sm:max-w-xl"
      >
        <SheetHeader className="border-b px-4 py-3">
          {/* "Sandbox images", not "Environments" — Project environments own
              the word "Environments" in UI now (naming decision 2026-07-24). */}
          <SheetTitle>Sandbox images</SheetTitle>
          <SheetDescription>
            A custom Docker image your computer boots from. Changing the image
            rebuilds the computer — all files on it will be deleted.
          </SheetDescription>
        </SheetHeader>

        {!showDetail ? (
          <EnvironmentList
            environments={environments}
            attachedEnvironmentId={attachedEnvironmentId}
            onSelect={(id) => {
              setCreating(false);
              setSelectedId(id);
            }}
            onNew={() => {
              setSelectedId(null);
              setCreating(true);
            }}
            onUseBase={() => void detachToBase()}
            attachToBaseDisabled={attachedEnvironmentId === null || !canAttach}
          />
        ) : creating ? (
          <NewEnvironmentForm
            projectId={projectId}
            onCancel={() => setCreating(false)}
            onCreated={(env) => {
              setCreating(false);
              setJustCreated(env);
              setSelectedId(env.environmentId);
            }}
          />
        ) : selected ? (
          <EnvironmentDetail
            key={selected.environmentId}
            env={selected}
            projectId={projectId}
            isAttached={attachedEnvironmentId === selected.environmentId}
            canAttach={canAttach}
            onBack={() => setSelectedId(null)}
            onDeleted={() => setSelectedId(null)}
          />
        ) : null}
      </SheetContent>
    </Sheet>
  );

  async function detachToBase() {
    try {
      await setComputerEnvironment({ projectId, environmentId: null });
      toast.success("Switched to the base image. Rebuilding your computer…");
    } catch (err) {
      toast.error(errMessage(err, "Could not switch to the base image."));
    }
  }
}

// ---------------------------------------------------------------------------

function EnvironmentList({
  environments,
  attachedEnvironmentId,
  onSelect,
  onNew,
  onUseBase,
  attachToBaseDisabled,
}: {
  environments: SandboxImageView[] | undefined;
  attachedEnvironmentId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onUseBase: () => void;
  attachToBaseDisabled: boolean;
}) {
  // Switching to the base image rebuilds the sandbox and wipes its disk, so
  // confirm the data loss before doing it (mirrors Reset / "Use on computer").
  const [confirmingBase, setConfirmingBase] = useState(false);

  if (environments === undefined) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading sandbox
        images…
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex-1 overflow-y-auto p-2">
        {/* Base image row */}
        <button
          type="button"
          onClick={() => setConfirmingBase(true)}
          disabled={attachToBaseDisabled}
          className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm hover:bg-muted/50 disabled:cursor-default disabled:opacity-100"
        >
          <span className="flex items-center gap-2">
            {attachedEnvironmentId === null ? (
              <Check className="h-4 w-4 text-primary" />
            ) : (
              <span className="h-4 w-4" />
            )}
            <span className="font-medium text-foreground">Base image</span>
            <span className="text-muted-foreground">
              Debian + Node + Python
            </span>
          </span>
          {attachedEnvironmentId !== null ? (
            <span className="text-xs text-muted-foreground">Use</span>
          ) : null}
        </button>
        {confirmingBase ? (
          <div className="mx-1 mt-1 flex flex-wrap items-center justify-between gap-2 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
            <span>
              Switch to the base image? All files on this computer will be
              deleted.
            </span>
            <span className="flex items-center gap-2">
              <Button
                size="sm"
                variant="destructive"
                onClick={() => {
                  setConfirmingBase(false);
                  onUseBase();
                }}
              >
                Switch
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setConfirmingBase(false)}
              >
                Cancel
              </Button>
            </span>
          </div>
        ) : null}

        {environments.length === 0 ? (
          <div className="px-3 py-8 text-center text-sm text-muted-foreground">
            No custom sandbox images yet — create one to customize your
            computer's image.
          </div>
        ) : (
          environments.map((env) => (
            <button
              key={env.environmentId}
              type="button"
              onClick={() => onSelect(env.environmentId)}
              className="flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-muted/50"
            >
              <span className="flex min-w-0 items-center gap-2">
                {attachedEnvironmentId === env.environmentId ? (
                  <Check className="h-4 w-4 shrink-0 text-primary" />
                ) : (
                  <span className="h-4 w-4 shrink-0" />
                )}
                <span className="truncate font-medium text-foreground">
                  {env.name}
                </span>
                {env.sharing === "project" ? (
                  <Users className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                ) : null}
              </span>
              <EnvironmentBuildBadge build={env.currentBuild} />
            </button>
          ))
        )}
      </div>
      <div className="border-t p-2">
        <Button size="sm" variant="outline" className="w-full" onClick={onNew}>
          <Plus className="mr-1.5 h-3.5 w-3.5" /> New sandbox image
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function NewEnvironmentForm({
  projectId,
  onCancel,
  onCreated,
}: {
  projectId: string;
  onCancel: () => void;
  onCreated: (env: SandboxImageView) => void;
}) {
  const createEnvironment = useCreateSandboxImage();
  const [name, setName] = useState("");
  const [dockerfile, setDockerfile] = useState(NEW_ENVIRONMENT_TEMPLATE);
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!name.trim()) {
      toast.error("Give the sandbox image a name.");
      return;
    }
    if (/REPLACE_WITH_DIGEST/.test(dockerfile)) {
      toast.error(
        "Pin the base image to a real @sha256 digest before creating."
      );
      return;
    }
    setSaving(true);
    try {
      const env = await createEnvironment({
        projectId,
        name: name.trim(),
        dockerfile,
      });
      toast.success(`Created “${env.name}”. Build it to use it.`);
      onCreated(env);
    } catch (err) {
      toast.error(errMessage(err, "Could not create the sandbox image."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 p-4">
      <button
        type="button"
        onClick={onCancel}
        className="flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-3.5 w-3.5" /> Back
      </button>
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Sandbox image name"
        className="rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-ring"
      />
      <DockerfileEditor value={dockerfile} onChange={setDockerfile} />
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button size="sm" onClick={() => void create()} disabled={saving}>
          {saving ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : null}
          Create
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function EnvironmentDetail({
  env,
  projectId,
  isAttached,
  canAttach,
  onBack,
  onDeleted,
}: {
  env: SandboxImageView;
  projectId: string;
  isAttached: boolean;
  canAttach: boolean;
  onBack: () => void;
  onDeleted: () => void;
}) {
  const updateEnvironment = useUpdateSandboxImage();
  const startBuild = useStartSandboxImageBuild();
  const promote = usePromoteSandboxImage();
  const deleteEnvironment = useDeleteSandboxImage();
  const setComputerEnvironment = useSetComputerSandboxImage();

  const [name, setName] = useState(env.name);
  const [dockerfile, setDockerfile] = useState(env.dockerfile);
  const [saving, setSaving] = useState(false);
  const [building, setBuilding] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [confirmingUse, setConfirmingUse] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Re-seed local buffers if the underlying env changes identity (the parent
  // remounts via `key`, but guard the reactive name/dockerfile too).
  useEffect(() => {
    setName(env.name);
    setDockerfile(env.dockerfile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [env.environmentId]);

  const build = env.currentBuild;
  const isShared = env.sharing === "project";
  // Compare against the TRIMMED name (what the backend stores), so trailing
  // whitespace never counts as a pending change and "dirty" clears after a save.
  const trimmedName = name.trim();
  const dirty = trimmedName !== env.name || dockerfile !== env.dockerfile;
  const readyToAttach = build?.status === "ready" && !dirty;

  const save = async (): Promise<boolean> => {
    setSaving(true);
    try {
      await updateEnvironment({
        environmentId: env.environmentId,
        ...(trimmedName !== env.name ? { name: trimmedName } : {}),
        ...(dockerfile !== env.dockerfile ? { dockerfile } : {}),
      });
      toast.success("Saved.");
      return true;
    } catch (err) {
      toast.error(errMessage(err, "Could not save."));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const runBuild = async () => {
    // Don't build the previously-persisted Dockerfile if saving the edits
    // failed — that would forge an image the user didn't actually save.
    if (dirty && !(await save())) return;
    setBuilding(true);
    try {
      const res = await startBuild({ environmentId: env.environmentId });
      toast.success(
        res.reused ? "Reused an existing build." : "Build started."
      );
    } catch (err) {
      toast.error(errMessage(err, "Could not start the build."));
    } finally {
      setBuilding(false);
    }
  };

  const useOnComputer = async () => {
    setAttaching(true);
    try {
      await setComputerEnvironment({
        projectId,
        environmentId: env.environmentId,
      });
      toast.success(`Using “${env.name}”. Rebuilding your computer…`);
    } catch (err) {
      // Includes the by-design rejection when the builder/computer providers
      // are incompatible (e.g. stub build + e2b computer).
      toast.error(errMessage(err, "Could not use this sandbox image."));
    } finally {
      setAttaching(false);
      setConfirmingUse(false);
    }
  };

  const onPromote = async () => {
    // Persist unsaved edits first so the project gets what the editor shows,
    // not the last-saved definition (mirrors Build's save-first behavior).
    if (dirty && !(await save())) return;
    try {
      await promote({ environmentId: env.environmentId });
      toast.success("Shared with the project.");
    } catch (err) {
      toast.error(
        errMessage(err, "Only project admins can share sandbox images.")
      );
    }
  };

  const onDelete = async () => {
    try {
      await deleteEnvironment({ environmentId: env.environmentId });
      toast.success("Sandbox image deleted.");
      onDeleted();
    } catch (err) {
      toast.error(
        errMessage(err, "Only project admins can delete shared sandbox images.")
      );
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 p-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> All sandbox images
        </button>
        <div className="flex items-center gap-2">
          <EnvironmentBuildBadge build={build} />
          {isAttached ? <Badge variant="outline">In use</Badge> : null}
        </div>
      </div>

      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="rounded-md border bg-background px-3 py-2 text-sm font-medium outline-none focus:ring-1 focus:ring-ring"
      />

      <DockerfileEditor value={dockerfile} onChange={setDockerfile} />

      {build?.status === "failed" && build.error ? (
        <div className="rounded border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
          {build.error}
        </div>
      ) : null}
      {build?.logPreview ? (
        <pre className="max-h-32 overflow-auto rounded border bg-muted/30 p-2 font-mono text-[11px] leading-snug text-muted-foreground">
          {build.logPreview}
        </pre>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        {dirty ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => void save()}
            disabled={saving}
          >
            {saving ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : null}
            Save
          </Button>
        ) : null}
        <Button size="sm" onClick={() => void runBuild()} disabled={building}>
          {building ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Hammer className="mr-1.5 h-3.5 w-3.5" />
          )}
          Build
        </Button>
        {confirmingUse ? (
          <span className="inline-flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            Change the image? All files on this computer will be deleted.
            <Button
              size="sm"
              variant="destructive"
              onClick={() => void useOnComputer()}
              disabled={attaching}
            >
              {attaching ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : null}
              Change
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setConfirmingUse(false)}
              disabled={attaching}
            >
              Cancel
            </Button>
          </span>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setConfirmingUse(true)}
            disabled={!readyToAttach || attaching || isAttached || !canAttach}
            title={
              !canAttach
                ? "Wait for the computer to be ready or asleep before changing its image"
                : readyToAttach
                ? undefined
                : "Build the sandbox image (and save changes) before using it"
            }
          >
            {isAttached ? "In use" : "Use on computer"}
          </Button>
        )}
      </div>

      <div className="mt-auto flex items-center justify-between border-t pt-3">
        {isShared ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <Users className="h-3.5 w-3.5" /> Shared with the project
          </span>
        ) : (
          <Button size="sm" variant="ghost" onClick={() => void onPromote()}>
            <Users className="mr-1.5 h-3.5 w-3.5" /> Share with project
          </Button>
        )}
        {confirmingDelete ? (
          <span className="inline-flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">Delete?</span>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => void onDelete()}
            >
              Delete
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setConfirmingDelete(false)}
            >
              Cancel
            </Button>
          </span>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            className="text-destructive hover:text-destructive"
            onClick={() => setConfirmingDelete(true)}
          >
            <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete
          </Button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function DockerfileEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      spellCheck={false}
      className="min-h-[180px] flex-1 resize-y rounded-md border bg-muted/20 p-3 font-mono text-xs leading-relaxed text-foreground outline-none focus:ring-1 focus:ring-ring"
    />
  );
}
